




import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import './AdminManager.css'; // Using the updated consolidated CSS file
import { usePortalMenuPosition, useOutsideClose } from '../../hooks/usePortalDropdown';
import { AccessDenied } from '../../shared-ui/States';

// --- SKELETON LOADER COMPONENT ---
const SkeletonLoader = ({ type = 'text', style }) => (
    <div className={`skeleton ${type}`} style={style}></div>
);

// --- Configuration ---
const AUTH_TOKEN_KEY = 'zTaskAuthToken';
const USER_DETAILS_KEY = 'zTaskUserDetails';
const API_BASE_URL = process.env.REACT_APP_SWAGGER_SERVICE_BASE_URL;

// --- API Endpoints ---
const GET_COMPANY_ENDPOINT = `${API_BASE_URL}/api/get-company`;
const GET_USERS_ENDPOINT = `${API_BASE_URL}/api/get-users`;
const GET_DEPARTMENTS_ENDPOINT = `${API_BASE_URL}/api/get-departments`;
const ADD_DEPARTMENT_ENDPOINT = `${API_BASE_URL}/api/add-department`;
const ASSIGN_DEPARTMENT_ENDPOINT = `${API_BASE_URL}/api/assign-department`;
const GET_DESIGNATIONS_ENDPOINT = `${API_BASE_URL}/api/get-designation`;
const CREATE_DESIGNATION_ENDPOINT = `${API_BASE_URL}/api/create-designation`;
const ASSIGN_DESIGNATION_ENDPOINT = `${API_BASE_URL}/api/assign-designation`;
const CREATE_HIERARCHY_ENDPOINT = `${API_BASE_URL}/api/create-hierarchy`;
const NOTIFY_USER_ENDPOINT = `${API_BASE_URL}/api/notify-user`;
const GET_MANAGERS_BY_USER_ENDPOINT = `${API_BASE_URL}/api/get-reporting-managers-by-userId`;
const GET_ROLES_ENDPOINT = `${API_BASE_URL}/api/get-roles`;
const ADMIN_CREATE_USER_ENDPOINT = `${API_BASE_URL}/api/admin/create-user`;
const DEFAULT_NEW_USER_PASSWORD = 'Welcome@123';
const DEACTIVATE_USER_ENDPOINT = `${API_BASE_URL}/api/deactivate-user`;
const REACTIVATE_USER_ENDPOINT = `${API_BASE_URL}/api/reactivate-user`;
const ADD_COST_OF_USER_ENDPOINT = `${API_BASE_URL}/api/cost-of-user`;
const GET_CURRENCIES_ENDPOINT = `${process.env.REACT_APP_NODE_SERVICE_BASE_URL}/api/get-column/common_master/currency`;


/**
 * A reusable dropdown component with search and clear functionality.
 */
const SearchableDropdown = ({
    options, value, onChange, placeholder, labelKey, valueKey, disabled, isLoading, renderOption,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const dropdownRef = useRef(null);
    const menuRef = useRef(null);
    const { menuStyle } = usePortalMenuPosition(isOpen, dropdownRef);
    useOutsideClose(isOpen, setIsOpen, dropdownRef, menuRef, () => setSearchTerm(''));

    const filteredOptions = useMemo(() => {
        if (!searchTerm) return options;
        const lowercasedTerm = searchTerm.toLowerCase();
        return options.filter(option => {
            const displayLabel = renderOption ? renderOption(option) : option[labelKey] || '';
            return displayLabel.toLowerCase().includes(lowercasedTerm);
        });
    }, [options, searchTerm, labelKey, renderOption]);

    const selectedOption = useMemo(() => {
        return options.find(option => String(option[valueKey]) === String(value));
    }, [options, value, valueKey]);

    const handleSelect = (option) => {
        onChange(option ? option[valueKey] : '');
        setIsOpen(false);
        setSearchTerm('');
    };

    const toggleDropdown = () => !disabled && !isLoading && setIsOpen(!isOpen);

    return (
        <div className={`searchable-dropdown-container${isOpen ? ' is-open' : ''}`} ref={dropdownRef}>
            <div
                className={`dropdown-header ${disabled ? 'disabled' : ''} ${isLoading ? 'loading' : ''}${isOpen ? ' open' : ''}`}
                onClick={toggleDropdown}
                tabIndex={disabled ? -1 : 0}
                onKeyDown={(e) => e.key === 'Enter' && toggleDropdown()}
            >
                 {isLoading ? (
                    <span className="placeholder">Loading...</span>
                ) : selectedOption ? (
                    <span className="selected-value">{renderOption ? renderOption(selectedOption) : selectedOption[labelKey]}</span>
                ) : (
                    <span className="placeholder">{placeholder}</span>
                )}
                <div className="dropdown-controls"><span className="dropdown-arrow"></span></div>
            </div>
            {isOpen && createPortal(
                <div className="dropdown-list-wrapper ui-portal-dropdown-menu" style={menuStyle} ref={menuRef}>
                    <div className="dropdown-search-container ui-portal-dropdown-search">
                        <input
                            type="text" className="dropdown-search-input" placeholder="Search..."
                            value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} autoFocus
                            onClick={(e) => e.stopPropagation()}
                        />
                        <span className="search-icon" />
                    </div>
                    <ul className="dropdown-list">
                        {!searchTerm && <li className="dropdown-list-clear-item" onClick={() => handleSelect(null)} tabIndex={0}>— Clear Selection —</li>}
                        {filteredOptions.length > 0 ? (
                            filteredOptions.map(option => (
                                <li key={option[valueKey]} onClick={() => handleSelect(option)} tabIndex={0}>
                                    {renderOption ? renderOption(option) : option[labelKey]}
                                </li>
                            ))
                        ) : (searchTerm && <li className="no-options">No results found</li>)}
                    </ul>
                </div>,
                document.body
            )}
        </div>
    );
};

/**
 * A multi-select dropdown component with search.
 */
const MultiSelectDropdown = ({
    options, value, onChange, placeholder, labelKey, valueKey, disabled, isLoading, renderOption,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const dropdownRef = useRef(null);
    const menuRef = useRef(null);
    const { menuStyle } = usePortalMenuPosition(isOpen, dropdownRef);
    useOutsideClose(isOpen, setIsOpen, dropdownRef, menuRef, () => setSearchTerm(''));

    const selectedOptions = useMemo(() =>
        options.filter(opt => value.includes(String(opt[valueKey]))),
        [options, value, valueKey]
    );

    const filteredOptions = useMemo(() => {
        const availableOptions = (options || []).filter((opt) => {
            const id = String(opt[valueKey]);
            return !value.map(String).includes(id);
        });
        if (!searchTerm) return availableOptions;
        const lowercasedTerm = searchTerm.toLowerCase();
        return availableOptions.filter(option => {
            const displayLabel = renderOption ? renderOption(option) : option[labelKey] || '';
            return String(displayLabel).toLowerCase().includes(lowercasedTerm);
        });
    }, [options, value, searchTerm, labelKey, valueKey, renderOption]);

    const handleSelect = (option) => {
        const selectedValue = String(option[valueKey]);
        const newValues = value.includes(selectedValue)
            ? value.filter(v => v !== selectedValue)
            : [...value, selectedValue];
        onChange(newValues);
        setSearchTerm('');
    };

    const handleRemove = (selectedValue) => {
        onChange(value.filter(v => v !== String(selectedValue)));
    };
    
    const toggleDropdown = () => !disabled && !isLoading && setIsOpen(!isOpen);

    return (
        <div className={`searchable-dropdown-container multi-select${isOpen ? ' is-open' : ''}`} ref={dropdownRef}>
            <div
                className={`dropdown-header ${disabled ? 'disabled' : ''} ${isLoading ? 'loading' : ''}${isOpen ? ' open' : ''}`}
                onClick={toggleDropdown}
                tabIndex={disabled ? -1 : 0}
            >
                {isLoading ? (
                    <span className="placeholder">Loading managers...</span>
                ) : selectedOptions.length > 0 ? (
                    <div className="selected-tags-container">
                        {selectedOptions.map(opt => (
                            <div key={opt[valueKey]} className="selected-tag">
                                {renderOption ? renderOption(opt) : opt[labelKey]}
                                <button className="remove-tag-btn" onClick={(e) => { e.stopPropagation(); handleRemove(opt[valueKey]); }}>&times;</button>
                            </div>
                        ))}
                    </div>
                ) : (
                    <span className="placeholder">{placeholder}</span>
                )}
                <div className="dropdown-controls"><span className="dropdown-arrow"></span></div>
            </div>
            {isOpen && createPortal(
                <div className="dropdown-list-wrapper ui-portal-dropdown-menu" style={menuStyle} ref={menuRef}>
                    <div className="dropdown-search-container ui-portal-dropdown-search">
                        <input
                            type="text" className="dropdown-search-input" placeholder="Search to add..."
                            value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} autoFocus
                            onClick={(e) => e.stopPropagation()}
                        />
                        <span className="search-icon" />
                    </div>
                    <ul className="dropdown-list">
                        {(options || []).length === 0 ? (
                            <li className="no-options">No other users available. Create/activate users first.</li>
                        ) : filteredOptions.length > 0 ? (
                            filteredOptions.map(option => (
                                <li key={option[valueKey]} onClick={() => handleSelect(option)} tabIndex={0}>
                                    {renderOption ? renderOption(option) : option[labelKey]}
                                </li>
                            ))
                        ) : (
                            <li className="no-options">
                                {searchTerm
                                    ? 'No results found'
                                    : (value.length > 0
                                        ? 'All available users are already selected above'
                                        : 'No managers to show')}
                            </li>
                        )}
                    </ul>
                </div>,
                document.body
            )}
        </div>
    );
};


const AdminManager = ({ onProfileUpdate }) => {
    // --- State Management ---
    const [currentUserRole, setCurrentUserRole] = useState(null);
    const [isLoadingRole, setIsLoadingRole] = useState(true);
    const [companyDetails, setCompanyDetails] = useState(null);

    // Data states
    const [users, setUsers] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [designations, setDesignations] = useState([]);
    const [currencies, setCurrencies] = useState([]);
    const [roles, setRoles] = useState([]);

    // Form State
    const [selectedUserId, setSelectedUserId] = useState('');
    const [selectedDepartmentId, setSelectedDepartmentId] = useState('');
    const [selectedDesignationId, setSelectedDesignationId] = useState('');
    const [selectedManagerIds, setSelectedManagerIds] = useState([]);
    const [userCost, setUserCost] = useState('');
    const [userCurrency, setUserCurrency] = useState('');

    // Add User (SUPER_ADMIN / ADMIN)
    const [isCreateUserModalOpen, setIsCreateUserModalOpen] = useState(false);
    const [newUserName, setNewUserName] = useState('');
    const [newUserEmail, setNewUserEmail] = useState('');
    const [newUserPassword, setNewUserPassword] = useState(DEFAULT_NEW_USER_PASSWORD);
    const [newUserRoleId, setNewUserRoleId] = useState('');
    const [isSubmittingCreateUser, setIsSubmittingCreateUser] = useState(false);

    // Submission states
    const [isSubmittingDept, setIsSubmittingDept] = useState(false);
    const [isSubmittingDesig, setIsSubmittingDesig] = useState(false);
    const [isSubmittingRM, setIsSubmittingRM] = useState(false);
    const [isSubmittingStatus, setIsSubmittingStatus] = useState(false);
    const [isSubmittingCost, setIsSubmittingCost] = useState(false);
    const [isFetchingManagers, setIsFetchingManagers] = useState(false);

    // Modals & UI Feedback
    const [isCreateDeptModalOpen, setIsCreateDeptModalOpen] = useState(false);
    const [newDepartmentName, setNewDepartmentName] = useState('');
    const [isSubmittingDeptCreate, setIsSubmittingDeptCreate] = useState(false);
    const [createDeptModalMessage, setCreateDeptModalMessage] = useState({ text: '', type: '' });
    const [isCreateDesigModalOpen, setIsCreateDesigModalOpen] = useState(false);
    const [newDesignationName, setNewDesignationName] = useState('');
    const [isFetchingData, setIsFetchingData] = useState(true);
    const [notification, setNotification] = useState({ message: '', type: '' });
    const [confirmation, setConfirmation] = useState({ isOpen: false, message: '', onConfirm: () => {}, confirmButtonText: 'Confirm' });

    // Non-SuperAdmin View States
    const [viewUsersWithDepartments, setViewUsersWithDepartments] = useState([]);
    const [isLoadingViewData, setIsLoadingViewData] = useState(false);
    const [viewDataError, setViewDataError] = useState('');

    // --- RBAC and Initial Data Fetch ---
    useEffect(() => {
        const storedUserDetailsString = localStorage.getItem(USER_DETAILS_KEY);
        let roleName = null;
        if (storedUserDetailsString) {
            try {
                const userDetails = JSON.parse(storedUserDetailsString);
                roleName = userDetails?.role?.name?.toUpperCase();
            } catch (e) { console.error("AdminManager: Error parsing user details:", e); }
        }
        setCurrentUserRole(roleName);
        setIsLoadingRole(false);
    }, []);

    const fetchCompanyDetails = useCallback(async () => {
        const token = localStorage.getItem(AUTH_TOKEN_KEY);
        if (!token) return;
        try {
            const response = await fetch(GET_COMPANY_ENDPOINT, { headers: { 'Authorization': `Bearer ${token}` } });
            if (!response.ok) throw new Error(`Failed to fetch company details: ${response.status}`);
            const data = await response.json();
            if (data.message && data.message.length > 0) setCompanyDetails(data.message[0]);
        } catch (error) { showNotification(error.message, 'error'); }
    }, []);

    const fetchInitialData = useCallback(async () => {
        setIsFetchingData(true);
        const token = localStorage.getItem(AUTH_TOKEN_KEY);
        if (!token) {
            showNotification('Authentication error. Please log in again.', 'error');
            setIsFetchingData(false);
            return;
        }
        const headers = { 'Authorization': `Bearer ${token}` };

        try {
            const [usersRes, deptsRes, desigsRes, currenciesRes, rolesRes] = await Promise.all([
                fetch(GET_USERS_ENDPOINT, { headers }),
                fetch(GET_DEPARTMENTS_ENDPOINT, { headers }),
                fetch(GET_DESIGNATIONS_ENDPOINT, { headers }),
                fetch(GET_CURRENCIES_ENDPOINT, { headers }),
                fetch(GET_ROLES_ENDPOINT, { headers }),
            ]);

            if (!usersRes.ok) throw new Error(`Failed to fetch users: ${usersRes.statusText}`);
            const usersData = await usersRes.json();
            if (usersData.userData) {
                const processedUsers = usersData.userData
                    .filter((u) => u && typeof u === 'object' && u.id != null && u.email)
                    .map((u) => ({
                        ...u,
                        id: String(u.id),
                        active: u.active !== false && u.active !== 'false' && u.active !== 0,
                        status: (u.active !== false && u.active !== 'false' && u.active !== 0) ? 'ACTIVE' : 'INACTIVE',
                    }));
                const sortedUsers = processedUsers.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
                setUsers(sortedUsers);
            } else {
                setUsers([]);
            }

            if (!deptsRes.ok) throw new Error(`Failed to fetch departments: ${deptsRes.statusText}`);
            const deptsData = await deptsRes.json();
            setDepartments(deptsData.departments ? deptsData.departments.map(d => ({ ...d, departmentId: String(d.departmentId) })) : []);

            if (!desigsRes.ok) throw new Error(`Failed to fetch designations: ${desigsRes.statusText}`);
            const desigsData = await desigsRes.json();
            setDesignations(desigsData.designations ? desigsData.designations.map(d => ({ ...d, designationId: String(d.designationId) })) : []);
            
            if (!currenciesRes.ok) throw new Error(`Failed to fetch currencies: ${currenciesRes.statusText}`);
            const currenciesData = await currenciesRes.json();
            setCurrencies(Array.isArray(currenciesData) ? currenciesData : []);

            if (rolesRes.ok) {
                const rolesData = await rolesRes.json();
                const roleList = Array.isArray(rolesData) ? rolesData : (rolesData.roles || rolesData.roleData || []);
                setRoles(
                    (roleList || [])
                        .filter((r) => r && r.roleId != null && String(r.name || '').toUpperCase() !== 'SUPER_ADMIN')
                        .map((r) => ({ ...r, roleId: String(r.roleId) }))
                );
            } else {
                setRoles([]);
            }

        } catch (error) {
            showNotification(error.message || 'Failed to load page data.', 'error');
        } finally {
            setIsFetchingData(false);
        }
    }, []);
    
    const fetchUserDepartmentDataForView = useCallback(async () => {
        if (isLoadingRole || currentUserRole === 'SUPER_ADMIN' || currentUserRole === 'ADMIN' || !currentUserRole) return;
        setIsLoadingViewData(true); setViewDataError('');
        const token = localStorage.getItem(AUTH_TOKEN_KEY);
        if (!token) { setViewDataError("Auth token missing."); setIsLoadingViewData(false); return; }
        try {
            const response = await fetch(GET_USERS_ENDPOINT, { headers: { 'Authorization': `Bearer ${token}` } });
            if (!response.ok) throw new Error(`View Users API Error: ${response.status}`);
            const data = await response.json(); setViewUsersWithDepartments(data.userData || []);
        } catch (err) { setViewDataError(err.message); }
        finally { setIsLoadingViewData(false); }
    }, [currentUserRole, isLoadingRole]);


    useEffect(() => {
        if (isLoadingRole) return;
        if (currentUserRole === 'SUPER_ADMIN' || currentUserRole === 'ADMIN') {
            fetchInitialData();
            fetchCompanyDetails();
        } else if (currentUserRole) {
            fetchUserDepartmentDataForView();
        }
    }, [currentUserRole, isLoadingRole, fetchInitialData, fetchUserDepartmentDataForView, fetchCompanyDetails]);

    
    useEffect(() => {
        const fetchUserManagers = async () => {
            if (!selectedUserId || users.length === 0) return;
            
            setIsFetchingManagers(true);
            setSelectedManagerIds([]);
            const token = localStorage.getItem(AUTH_TOKEN_KEY);

            try {
                const response = await fetch(`${GET_MANAGERS_BY_USER_ENDPOINT}?userId=${selectedUserId}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (!response.ok) throw new Error('Could not fetch reporting managers.');
                
                const data = await response.json();
                if (data.message && Array.isArray(data.message)) {
                    const managerEmails = data.message.map(m => m.managerEmail);
                    const managerIds = users
                        .filter(user => managerEmails.includes(user.email))
                        .map(user => user.id);
                    setSelectedManagerIds(managerIds);
                }
            } catch (error) {
                showNotification(error.message, 'error');
            } finally {
                setIsFetchingManagers(false);
            }
        };

        if (selectedUserId) {
            const selectedUser = users.find(u => u.id === selectedUserId);
            if (selectedUser) {
                setSelectedDepartmentId(selectedUser.department?.departmentId || '');
                setSelectedDesignationId(selectedUser.designation?.designationId || '');
                setUserCost(selectedUser.perDayRate || '');
                setUserCurrency(selectedUser.currency || '');
                fetchUserManagers();
            }
        } else {
            setSelectedDepartmentId('');
            setSelectedDesignationId('');
            setSelectedManagerIds([]);
            setUserCost('');
            setUserCurrency('');
        }
    }, [selectedUserId, users]);


    const showNotification = (message, type, duration = 4000) => {
        setNotification({ message, type });
        setTimeout(() => setNotification({ message: '', type: '' }), duration);
    };

    const resetCreateUserForm = () => {
        setNewUserName('');
        setNewUserEmail('');
        setNewUserPassword(DEFAULT_NEW_USER_PASSWORD);
        setNewUserRoleId('');
    };

    const handleCreateUserSubmit = async (e) => {
        e.preventDefault();
        const token = localStorage.getItem(AUTH_TOKEN_KEY);
        if (!token) {
            showNotification('Authentication error. Please log in again.', 'error');
            return;
        }
        if (!newUserName.trim() || !newUserEmail.trim() || !newUserPassword.trim()) {
            showNotification('Name, email and password are required.', 'error');
            return;
        }
        setIsSubmittingCreateUser(true);
        try {
            const payload = {
                name: newUserName.trim(),
                email: newUserEmail.trim().toLowerCase(),
                password: newUserPassword,
                active: true,
            };
            if (newUserRoleId) payload.roleId = Number(newUserRoleId);
            const response = await fetch(ADMIN_CREATE_USER_ENDPOINT, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                },
                body: JSON.stringify(payload),
            });
            const data = await response.json().catch(() => ({}));
            if (!response.ok) throw new Error(data.message || `Create user failed (${response.status})`);
            showNotification(
                data.message
                    || `User ${payload.email} created. Welcome email includes login URL and temporary password.`,
                data.welcomeEmailSent === false ? 'error' : 'success',
                8000
            );
            setIsCreateUserModalOpen(false);
            resetCreateUserForm();
            await fetchInitialData();
            if (data.user?.id) setSelectedUserId(String(data.user.id));
        } catch (err) {
            showNotification(err.message || 'Failed to create user.', 'error');
        } finally {
            setIsSubmittingCreateUser(false);
        }
    };

    const handleOpenModal = (modalSetter) => modalSetter(true);
    const handleCloseModal = (modalSetter, stateResetter) => {
        if (isSubmittingDeptCreate || isSubmittingDesig) return;
        modalSetter(false);
        if (stateResetter) stateResetter('');
    };
    
    const handleCloseConfirmation = () => {
        if (isSubmittingDesig || isSubmittingRM || isSubmittingStatus || isSubmittingCost) return;
        setConfirmation({ isOpen: false, message: '', onConfirm: () => {}, confirmButtonText: 'Confirm' });
    };

    const handleConfirmAction = async () => {
        await confirmation.onConfirm();
        handleCloseConfirmation();
    };

    const handleCreateDepartmentSubmit = async (event) => {
        event.preventDefault();
        const trimmedName = newDepartmentName.trim();
        if (!trimmedName) {
            setCreateDeptModalMessage({ text: 'Department Name is required.', type: 'error' });
            return;
        }
        setIsSubmittingDeptCreate(true);
        const token = localStorage.getItem(AUTH_TOKEN_KEY);
        try {
            const response = await fetch(`${ADD_DEPARTMENT_ENDPOINT}?departmentName=${trimmedName}`, {
                method: 'POST', headers: { 'Authorization': `Bearer ${token}` }
            });
            const resData = await response.json();
            if (!response.ok) throw new Error(resData.message || 'Failed to create department');
            showNotification(resData.message || 'Department created!', 'success');
            fetchInitialData();
            handleCloseModal(setIsCreateDeptModalOpen, setNewDepartmentName);
        } catch (error) {
            setCreateDeptModalMessage({ text: `Creation Failed: ${error.message}`, type: 'error' });
        } finally {
            setIsSubmittingDeptCreate(false);
        }
    };
    
    const handleAssignDepartment = async () => {
        if (!selectedUserId || !selectedDepartmentId) return;
        setIsSubmittingDept(true);
        const token = localStorage.getItem(AUTH_TOKEN_KEY);
        try {
            const response = await fetch(ASSIGN_DEPARTMENT_ENDPOINT, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ userId: selectedUserId, departmentId: selectedDepartmentId })
            });
            const resData = await response.json();
            if (!response.ok) throw new Error(resData.message || 'API Error');
            showNotification(resData.message || 'Department assigned!', 'success');
            fetchInitialData();
        } catch (error) {
            showNotification(`Assignment Failed: ${error.message}`, 'error');
        } finally {
            setIsSubmittingDept(false);
        }
    };

    const performCreateDesignation = async () => {
        setIsSubmittingDesig(true);
        try {
            const url = new URL(CREATE_DESIGNATION_ENDPOINT);
            url.searchParams.append('name', newDesignationName.trim());
            const response = await fetch(url.toString(), {
                method: 'POST', headers: { 'Authorization': `Bearer ${localStorage.getItem(AUTH_TOKEN_KEY)}` }
            });
            if (response.ok) {
                showNotification('Designation created successfully!', 'success');
                fetchInitialData();
                handleCloseModal(setIsCreateDesigModalOpen, setNewDesignationName);
            } else {
                const errorData = await response.json();
                showNotification(errorData.message || 'Failed to create.', 'error');
            }
        } catch (error) {
            showNotification('An error occurred.', 'error');
        } finally {
            setIsSubmittingDesig(false);
        }
    };

    const handleCreateDesignation = (e) => {
        e.preventDefault();
        if (!newDesignationName.trim()) return;
        setConfirmation({
            isOpen: true,
            message: `Create designation "${newDesignationName.trim()}"?`,
            onConfirm: performCreateDesignation,
            confirmButtonText: 'Yes, Create'
        });
    };

    const handleAssignDesignation = () => {
        if (!selectedUserId || !selectedDesignationId) return;
        const userName = users.find(u => u.id === selectedUserId)?.name || 'user';
        const designationName = designations.find(d => d.designationId === selectedDesignationId)?.designationName || 'designation';
        
        const performAssign = async () => {
            setIsSubmittingDesig(true);
            try {
                const url = new URL(ASSIGN_DESIGNATION_ENDPOINT);
                url.searchParams.append('userId', selectedUserId);
                url.searchParams.append('designationId', selectedDesignationId);
                const response = await fetch(url.toString(), {
                    method: 'POST', headers: { 'Authorization': `Bearer ${localStorage.getItem(AUTH_TOKEN_KEY)}` }
                });
                if (response.ok) {
                    showNotification('Designation assigned successfully!', 'success');
                    if (onProfileUpdate) onProfileUpdate();
                    fetchInitialData();
                } else {
                    const errorData = await response.json();
                    showNotification(errorData.message || 'Failed to assign.', 'error');
                }
            } catch (error) {
                showNotification('An error occurred.', 'error');
            } finally {
                setIsSubmittingDesig(false);
            }
        };

        setConfirmation({
            isOpen: true,
            message: `Assign "${designationName}" to ${userName}?`,
            onConfirm: performAssign,
            confirmButtonText: 'Yes, Assign'
        });
    };
    
    const performUserStatusChange = async (action) => {
        if (!selectedUserId) return;

        setIsSubmittingStatus(true);
        const token = localStorage.getItem(AUTH_TOKEN_KEY);
        const endpoint = action === 'deactivate' ? DEACTIVATE_USER_ENDPOINT : REACTIVATE_USER_ENDPOINT;
        const url = new URL(endpoint);
        url.searchParams.append('userId', selectedUserId);

        try {
            const response = await fetch(url.toString(), {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || `Failed to ${action} user.`);
            }
            
            const successMessage = await response.json();
            showNotification(successMessage.message || `User ${action}d successfully!`, 'success');
            fetchInitialData();
            
        } catch (error) {
            showNotification(error.message, 'error');
        } finally {
            setIsSubmittingStatus(false);
        }
    };

    const handleUserStatusChange = () => {
        const user = users.find(u => u.id === selectedUserId);
        if (!user) return;

        const action = user.status === 'ACTIVE' ? 'deactivate' : 'reactivate';
        const actionText = user.status === 'ACTIVE' ? 'Deactivate' : 'Reactivate';

        setConfirmation({
            isOpen: true,
            message: `Are you sure you want to ${action} the user "${user.name}"?`,
            onConfirm: () => performUserStatusChange(action),
            confirmButtonText: `Yes, ${actionText}`
        });
    };

    const handleSetUserCost = () => {
        const rate = parseFloat(userCost);
        if (isNaN(rate) || rate < 0) {
            showNotification('Rate must be a valid, non-negative number.', 'error');
            return;
        }

        if (!selectedUserId || !userCurrency) {
            showNotification('Please provide both a rate and a currency.', 'error');
            return;
        }
        const user = users.find(u => u.id === selectedUserId);
        if (!user) return;

        const performCostAssignment = async () => {
            setIsSubmittingCost(true);
            const token = localStorage.getItem(AUTH_TOKEN_KEY);
            
            const url = new URL(ADD_COST_OF_USER_ENDPOINT);
            url.searchParams.append('userId', selectedUserId);
            url.searchParams.append('ratePerDay', rate);
            url.searchParams.append('currency', userCurrency);
            
            try {
                const response = await fetch(url.toString(), {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${token}` }
                });

                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    throw new Error(errorData.message || 'Failed to set user cost.');
                }
                
                showNotification('User cost updated successfully!', 'success');
                fetchInitialData();

            } catch (error) {
                showNotification(error.message, 'error');
            } finally {
                setIsSubmittingCost(false);
            }
        };

        setConfirmation({
            isOpen: true,
            message: `Set ${user.name}'s rate to ${rate} ${userCurrency} per day?`,
            onConfirm: performCostAssignment,
            confirmButtonText: 'Yes, Set Cost'
        });
    };


    const handleAssignManager = () => {
        if (!selectedUserId || selectedManagerIds.length === 0) return;

        const user = users.find(u => u.id === selectedUserId);
        const managers = users.filter(u => selectedManagerIds.includes(u.id));
        if (!user || managers.length === 0) return;

        const managerNames = managers.map(m => m.name).join(', ');

        const performRMAssignment = async () => {
            setIsSubmittingRM(true);
            const token = localStorage.getItem(AUTH_TOKEN_KEY);
            const headers = { 'Authorization': `Bearer ${token}` };

            const hierarchyUrl = new URL(CREATE_HIERARCHY_ENDPOINT);
            managers.forEach(manager => {
                hierarchyUrl.searchParams.append('parent_user', manager.email);
            });
            hierarchyUrl.searchParams.append('children_users', user.email);

            try {
                const response = await fetch(hierarchyUrl.toString(), { method: 'POST', headers });

                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    throw new Error(errorData.message || `Failed to assign managers. Status: ${response.status}`);
                }

                const notifyUrl = new URL(NOTIFY_USER_ENDPOINT);
                notifyUrl.searchParams.append('email', user.email);
                await fetch(notifyUrl.toString(), { method: 'POST', headers });
                
                showNotification(`Manager(s) assigned to ${user.name} and notification sent!`, 'success');
                
            } catch (error) {
                showNotification(error.message, 'error', 6000);
            } finally {
                setIsSubmittingRM(false);
            }
        };

        setConfirmation({
            isOpen: true,
            message: `Assign ${managerNames} as manager(s) for ${user.name}? This will update the hierarchy and send a notification email.`,
            onConfirm: performRMAssignment,
            confirmButtonText: 'Yes, Assign & Notify'
        });
    };
    
    // --- Render Logic ---
    if (isLoadingRole) return <div className="loading-indicator">Verifying user access...</div>;

    const availableManagers = users.filter((user) => {
        if (!user || user.id == null || !user.email) return false;
        if (String(user.id) === String(selectedUserId)) return false;
        if (user.status === 'INACTIVE' || user.active === false) return false;
        return true;
    });
    const renderUserOption = (user) => `${user.name || 'Unknown'} (${user.email || 'no-email'})${user.status === 'INACTIVE' ? ' (Inactive)' : ''}`;
    
    if (currentUserRole === 'SUPER_ADMIN' || currentUserRole === 'ADMIN') {
        const selectedUser = users.find(u => u.id === selectedUserId);

        if (isFetchingData) {
            return (
                <div className="admin-manager-container">
                    <header className="admin-page-header"><SkeletonLoader type="title" style={{ width: '40%' }} /><SkeletonLoader type="text" style={{ width: '60%' }} /></header>
                    <div className="content-card">
                        <SkeletonLoader type="title" style={{ width: '60%' }} /><div className="form-group"><SkeletonLoader type="label" /><SkeletonLoader type="input" /></div>
                        {[...Array(3)].map((_, index) => (
                           <div className="form-group-inline" key={index}><div style={{ flex: 1 }}><SkeletonLoader type="label" /><SkeletonLoader type="input" /></div><SkeletonLoader type="button" /></div>
                        ))}
                    </div>
                     <div className="content-card list-card"><SkeletonLoader type="title" style={{ width: '25%' }} /><div className="department-list">{[...Array(4)].map((_, index) => (<SkeletonLoader key={index} type="tag" />))}</div></div>
                </div>
            );
        }

        return (
            <div className="admin-manager-container">
                {notification.message && <div className={`notification ${notification.type}`}>{notification.message}</div>}
    
                {confirmation.isOpen && (
                     <div className="modal-overlay" onClick={handleCloseConfirmation}>
                         <div className="modal-content confirmation-modal" onClick={e => e.stopPropagation()}>
                             <div className="modal-header"><h3 className="modal-title">Confirm Action</h3></div>
                             <div className="modal-body"><p className="confirmation-text">{confirmation.message}</p></div>
                             <div className="modal-footer">
                                 <button type="button" className="button-secondary" onClick={handleCloseConfirmation} disabled={isSubmittingDesig || isSubmittingRM || isSubmittingStatus || isSubmittingCost}>Cancel</button>
                                 <button type="button" className="button-primary green" onClick={handleConfirmAction} disabled={isSubmittingDesig || isSubmittingRM || isSubmittingStatus || isSubmittingCost}>
                                     {(isSubmittingDesig || isSubmittingRM || isSubmittingStatus || isSubmittingCost) ? 'Processing...' : confirmation.confirmButtonText}
                                 </button>
                             </div>
                         </div>
                     </div>
                )}
    
                {isCreateDeptModalOpen && (
                    <div className="modal-overlay">
                        <div className="modal-content" onClick={e => e.stopPropagation()}>
                            <h2>Create New Department</h2>
                            <form onSubmit={handleCreateDepartmentSubmit}>
                                {createDeptModalMessage.text && <div className={`form-message ${createDeptModalMessage.type}`}>{createDeptModalMessage.text}</div>}
                                <div className="form-group">
                                    <label htmlFor="newDepartmentNameModal">Department Name <span className="required-indicator">*</span></label>
                                    <input type="text" id="newDepartmentNameModal" className="form-input" value={newDepartmentName} onChange={(e) => setNewDepartmentName(e.target.value)} disabled={isSubmittingDeptCreate} required autoFocus />
                                </div>
                                <div className="modal-actions">
                                    <button type="button" onClick={() => handleCloseModal(setIsCreateDeptModalOpen, setNewDepartmentName)} className="button-secondary" disabled={isSubmittingDeptCreate}>Cancel</button>
                                    <button type="submit" className="button-primary" disabled={isSubmittingDeptCreate}>{isSubmittingDeptCreate ? 'Creating...' : 'Create'}</button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {isCreateDesigModalOpen && (
                    <div className="modal-overlay" onClick={() => handleCloseModal(setIsCreateDesigModalOpen, setNewDesignationName)}>
                        <div className="modal-content" onClick={e => e.stopPropagation()}>
                            <form onSubmit={handleCreateDesignation}>
                                <div className="modal-header"><h3 className="modal-title">Create New Designation</h3></div>
                                <div className="modal-body">
                                    <label htmlFor="newDesignationName" className="form-label">Designation Name <span className="required-indicator">*</span></label>
                                    <input id="newDesignationName" type="text" className="form-input" value={newDesignationName} onChange={(e) => setNewDesignationName(e.target.value)} disabled={isSubmittingDesig} autoFocus required />
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="button-secondary" onClick={() => handleCloseModal(setIsCreateDesigModalOpen, setNewDesignationName)} disabled={isSubmittingDesig}>Cancel</button>
                                    <button type="submit" className="button-primary" disabled={isSubmittingDesig}>+ Create</button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                <header className="admin-page-header" style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                    <div>
                        <h1 className="admin-page-title">User & Organization Management</h1>
                        <p className="admin-page-subtitle">Add users to your organization, then assign department, designation, cost, and reporting hierarchy.</p>
                    </div>
                    {(currentUserRole === 'SUPER_ADMIN' || currentUserRole === 'ADMIN') && (
                        <button
                            type="button"
                            className="button-primary"
                            onClick={() => { resetCreateUserForm(); setIsCreateUserModalOpen(true); }}
                        >
                            + Add User
                        </button>
                    )}
                </header>

                {isCreateUserModalOpen && (
                    <div className="modal-overlay" onClick={() => !isSubmittingCreateUser && (setIsCreateUserModalOpen(false), resetCreateUserForm())}>
                        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                            <form onSubmit={handleCreateUserSubmit}>
                                <div className="modal-header">
                                    <h3 className="modal-title">Add User to Your Organization</h3>
                                </div>
                                <div className="modal-body">
                                    <p className="field-help-text" style={{ marginBottom: '1rem' }}>
                                        Creates the user under your tenant only (not public signup). Share the default password, then ask them to change it from Profile or Forgot Password.
                                    </p>
                                    <div className="form-group">
                                        <label className="form-label">Full Name <span className="required-indicator">*</span></label>
                                        <input className="form-input" value={newUserName} onChange={(e) => setNewUserName(e.target.value)} required disabled={isSubmittingCreateUser} autoFocus />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Email (login) <span className="required-indicator">*</span></label>
                                        <input type="email" className="form-input" value={newUserEmail} onChange={(e) => setNewUserEmail(e.target.value)} required disabled={isSubmittingCreateUser} />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Default Password <span className="required-indicator">*</span></label>
                                        <input className="form-input" value={newUserPassword} onChange={(e) => setNewUserPassword(e.target.value)} required disabled={isSubmittingCreateUser} />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Role</label>
                                        <SearchableDropdown
                                            options={roles}
                                            value={newUserRoleId}
                                            onChange={setNewUserRoleId}
                                            placeholder="-- Default: USER --"
                                            labelKey="name"
                                            valueKey="roleId"
                                            disabled={isSubmittingCreateUser}
                                        />
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="button-secondary" disabled={isSubmittingCreateUser} onClick={() => { setIsCreateUserModalOpen(false); resetCreateUserForm(); }}>Cancel</button>
                                    <button type="submit" className="button-primary" disabled={isSubmittingCreateUser}>{isSubmittingCreateUser ? 'Creating...' : 'Create User'}</button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                <div className="content-card">
                    <div className="form-group">
                        <label className="form-label master-label">Select User to Manage *</label>
                        <SearchableDropdown options={users} value={selectedUserId} onChange={setSelectedUserId} placeholder="-- Select a User --" labelKey="name" valueKey="id" renderOption={renderUserOption} />
                    </div>

                    {selectedUser && (
                        <div className="user-status-management">
                            <p>
                                User Status: <span className={`status-badge ${selectedUser.status === 'ACTIVE' ? 'active' : 'inactive'}`}>{selectedUser.status}</span>
                            </p>
                            <button 
                                onClick={handleUserStatusChange}
                                className={`button-secondary ${selectedUser.status === 'ACTIVE' ? 'danger' : 'safe'}`} 
                                disabled={isSubmittingStatus}
                            >
                                {isSubmittingStatus ? 'Updating...' : (selectedUser.status === 'ACTIVE' ? 'Deactivate User' : 'Reactivate User')}
                            </button>
                        </div>
                    )}
                    
                    <div className="assignments-container" style={{ opacity: selectedUserId ? 1 : 0.5, transition: 'opacity 0.3s' }}>
                        
                        {/* --- UPDATED: User Cost Section with individual labels --- */}
                        {selectedUser && companyDetails && companyDetails.companyKeyword === 'ZO' && (
                            <div className="form-group-inline">
                                <div className="form-group-main cost-group">
                                    <div className="cost-inputs">
                                        <div className="form-group">
                                            <label className="form-label">User per day rate</label>
                                            <input 
                                                type="number" 
                                                placeholder="Rate per Day" 
                                                className="form-input cost-rate-input"
                                                value={userCost}
                                                onChange={(e) => setUserCost(e.target.value)}
                                                disabled={!selectedUserId || isSubmittingCost}
                                                min="0"
                                                step="any"
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label">Currency</label>
                                            <SearchableDropdown 
                                                options={currencies} 
                                                value={userCurrency} 
                                                onChange={setUserCurrency} 
                                                placeholder="Currency" 
                                                labelKey="value" 
                                                valueKey="value" 
                                                disabled={!selectedUserId || isSubmittingCost}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <button onClick={handleSetUserCost} className="button-primary assign-button" disabled={!selectedUserId || !userCost || !userCurrency || isSubmittingCost}>
                                    {isSubmittingCost ? 'Saving...' : 'Set Cost'}
                                </button>
                            </div>
                        )}

                        <div className="form-group-inline">
                            <div className="form-group-main">
                                <label className="form-label">Assign Department</label>
                                <SearchableDropdown options={departments} value={selectedDepartmentId} onChange={setSelectedDepartmentId} placeholder="-- Select a Department --" labelKey="departmentName" valueKey="departmentId" disabled={!selectedUserId || isSubmittingDept} />
                            </div>
                            <button onClick={handleAssignDepartment} className="button-primary assign-button" disabled={!selectedUserId || !selectedDepartmentId || isSubmittingDept}>{isSubmittingDept ? 'Assigning...' : 'Assign'}</button>
                        </div>
                        {companyDetails && companyDetails.companyKeyword !== 'PL' && <button className="button-link" onClick={() => handleOpenModal(setIsCreateDeptModalOpen)}>+ Create New Department</button>}
                        
                        <div className="form-group-inline">
                            <div className="form-group-main">
                                <label className="form-label">Assign Designation</label>
                                <SearchableDropdown options={designations} value={selectedDesignationId} onChange={setSelectedDesignationId} placeholder="-- Select a Designation --" labelKey="designationName" valueKey="designationId" disabled={!selectedUserId || isSubmittingDesig} />
                            </div>
                            <button onClick={handleAssignDesignation} className="button-primary assign-button" disabled={!selectedUserId || !selectedDesignationId || isSubmittingDesig}>{isSubmittingDesig ? 'Assigning...' : 'Assign'}</button>
                        </div>
                        <button className="button-link" onClick={() => handleOpenModal(setIsCreateDesigModalOpen)}>+ Create New Designation</button>
                        
                        <div className="form-group-inline">
                            <div className="form-group-main">
                                <label className="form-label">Assign Reporting Manager(s)</label>
                                <p className="field-help-text">
                                    Sets who this user reports to (team hierarchy). Used for Task visibility and escalation. Pick one or more managers, then click Assign &amp; Notify.
                                </p>
                                <MultiSelectDropdown 
                                    options={availableManagers} 
                                    value={selectedManagerIds} 
                                    onChange={setSelectedManagerIds} 
                                    placeholder="-- Select one or more managers --" 
                                    labelKey="name" 
                                    valueKey="id" 
                                    disabled={!selectedUserId || isSubmittingRM} 
                                    isLoading={isFetchingManagers}
                                    renderOption={renderUserOption} 
                                />
                            </div>
                            <button onClick={handleAssignManager} className="button-primary assign-button green" disabled={!selectedUserId || selectedManagerIds.length === 0 || isSubmittingRM}>{isSubmittingRM ? 'Assigning...' : 'Assign & Notify'}</button>
                        </div>
                    </div>
                </div>

                 <div className="content-card list-card">
                    <h3>Existing Departments</h3>
                    {departments.length > 0 ? (<ul className="department-list">{departments.map(dept => (<li key={dept.departmentId} className="department-list-item">{dept.departmentName}</li>))}</ul>) : (<p className="info-message">No departments found.</p>)}
                </div>
                
                 <div className="content-card list-card">
                    <h3>Existing Designations</h3>
                    {designations.length > 0 ? (<ul className="department-list">{designations.map(desig => (<li key={desig.designationId} className="department-list-item">{desig.designationName}</li>))}</ul>) : (<p className="info-message">No designations found.</p>)}
                </div>
            </div>
        );
    }
    else if(currentUserRole) {
        return (
            <div className="user-departments-view-container">
                <div className="main-header view-header"><h2>User Department Overview</h2><p>List of users and their assigned departments.</p></div>
                {isLoadingViewData && <div className="view-loading-container">Loading...</div>}
                {viewDataError && <div className="view-error-container">Error: {viewDataError}</div>}
                {!isLoadingViewData && !viewDataError && (viewUsersWithDepartments.length === 0 ? (<p>No users found.</p>) : (
                    <table className="user-departments-table">
                        <thead><tr><th>ID</th><th>Name</th><th>Email</th><th>Department</th></tr></thead>
                        <tbody>{viewUsersWithDepartments.map(user => (<tr key={user.id}><td>{user.id}</td><td>{user.name || 'N/A'}</td><td>{user.email}</td><td>{user.department?.departmentName || 'Not Assigned'}</td></tr>))}</tbody>
                    </table>
                ))}
            </div>
        );
    }
    else {
        return (
            <AccessDenied description="Please log in with appropriate permissions." />
        );
    }
};

export default AdminManager;